// src/pages/profile/patient/PatientProfile.jsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import DoctorResultCard from "@/components/doctor/DoctorResultCard.jsx";
import TopBarProfile from "@/sot/primitives/TopBarProfile.jsx";
import CoverPhoto from "@/sot/primitives/CoverPhoto.jsx";
import AvatarHeaderProfile from "@/sot/primitives/AvatarHeaderProfile.jsx";
import Divider from "@/components/ui/Divider.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";
import { loadPatientIntroPreview } from "@/lib/patientIntroPreview";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import { normalizeVerificationStatus } from "@/utils/verificationStatus.js";
import { resolveDependantAvatarSrc } from "@/services/dependantCareService.js";
import { hydrateDependantIdentityStatuses } from "@/services/dependantIdentityVerificationService";
import Card from "@/components/ui/Card.jsx";
import {
  readAvatarSessionCache,
  writeAvatarSessionCache,
} from "@/lib/avatarSessionCache.js";
import {
  listMyPastAppointmentSnapshots,
  listPastAppointmentSnapshotsForPatient,
} from "@/services/patientAppointmentSnapshots.js";
import {
  buildFavoriteDoctorIds,
  getFavoriteDoctorIdsForUser,
  isFavoriteDoctor,
  listFavoriteDoctorIdsForPatient,
  subscribeDoctorFavorites,
} from "@/services/doctorFavorites.js";

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

const COVER_IMAGE_LIGHT = "/assets/images/patient_profile_cover.png?v=20260323";
const COVER_IMAGE_DARK = "/assets/images/patient_profile_cover.svg?v=20260323";

// section icons (44 x 44 in cards)
const ICON_CONTACT_SECTION = "/icons/patient/contact_data.svg";
const ICON_ADDRESS_SECTION = "/icons/patient/residence_address.svg";
const ICON_MEDICAL_SECTION = "/icons/patient/medical_history.svg";
const ICON_MEDICATIONS = "/icons/pill.svg";

// row icons
const ICON_PHONE = "/icons/patient/call.svg";
const ICON_EMAIL = "/icons/patient/mail.svg";
const ICON_NATIONAL_ID = "/icons/patient/fingerprint.svg";

const ICON_HEALTH_INSURANCE_PROFILE = "/icons/patient/health_and_safety-profile.svg";
const ICON_HEALTH_INSURANCE = "/icons/patient/health_and_safety.svg";
const ICON_DEPENDANT = "/icons/patient/dependant_profile.svg";
const ICON_DEPENDANT_CHILD = "/icons/dependant_child.svg";
const ICON_DEPENDANT_ADULT = "/icons/dependant_adult.svg";
const ICON_VERIFIED_CHIP = "/icons/doctor/verified_badge.svg";
const ICON_PENDING_VERIFICATION = "/icons/doctor/pending-verification-badge.svg";
const DOCTOR_AVATAR_FALLBACK = "/icons/doctor/avatar_placeholder.svg";

const ICON_ADDRESS = "/icons/patient/Address.svg";
const ICON_POSTAL = "/icons/patient/mail.svg";
const ICON_DISTANCE = "/icons/patient/distance.svg";
const ICON_MAP_PIN_FILLED = "/icons/patient/distance_filled.svg";
const ICON_MEDICAL_SERVICES = "/icons/patient/medical_services_filled.svg";
const ICON_STAR_FILLED = "/icons/patient/star_filled.svg";

const ICON_BLOODTYPE = "/icons/patient/bloodtype.svg";
const ICON_ALLERGY = "/icons/patient/allergy.svg";
const ICON_CONDITIONS = "/icons/patient/conditions.svg";
const ICON_PULMONOLOGY = "/icons/patient/pulmonology.svg";
const ICON_HEIGHT_WEIGHT = "/icons/height-weight.svg";

const ICON_FAVORITE = "/icons/patient/favorite.svg";

const DEPENDANTS_DEV_KEY = "afrimed:dev:patient_dependants";
const INSURANCE_DEV_KEY = "afrimed:dev:patient_insurance";
const PATIENT_NOTIFICATIONS_KEY = "afrimed.notifications.patient";

function isFrenchLanguageCode(languageCode = "") {
  return String(languageCode || "").trim().toLowerCase().startsWith("fr");
}

// Demo Patient photos (dev only)
const PATIENT_PHOTOS_1 = "/icons/doctor/appointments/skin-rash.jpg";
const PATIENT_PHOTOS_2 = "/icons/doctor/appointments/skin-rash_scroll.jpg";
const PATIENT_PHOTOS_3 = "/icons/doctor/appointments/symptoms of monkeypox disease.jpg";
const PATIENT_PHOTOS_4 = "/icons/doctor/appointments/red spots_4295.jpg";
const PATIENT_PHOTOS_5 = "/icons/doctor/appointments/acne scars_6929.jpg";
const PATIENT_PHOTOS_6 = "/icons/doctor/appointments/acne scars_0582.jpg";
const PATIENT_PHOTOS_7 = "/icons/doctor/appointments/monkeypox disease_3737.jpg";
const PATIENT_PHOTOS_8 = "/icons/doctor/appointments/monkeypox disease_8061.jpg";

const PATIENT_PHOTOS_FALLBACK = [
  PATIENT_PHOTOS_1,
  PATIENT_PHOTOS_2,
  PATIENT_PHOTOS_3,
  PATIENT_PHOTOS_4,
  PATIENT_PHOTOS_5,
  PATIENT_PHOTOS_6,
  PATIENT_PHOTOS_7,
  PATIENT_PHOTOS_8,
];
const PATIENT_PHOTOS_KEY_PREFIX = "afrimed_patient_photos_v1:";
const PATIENT_BUCKET = "patient-profile-images";

function getPatientPhotoKeys(profile, authUser) {
  const ids = [
    profile?.id,
    profile?.user_id,
    profile?.uid,
    profile?.patient_id,
    authUser?.id,
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean);

  const uniqueIds = Array.from(new Set(ids));
  return uniqueIds.map((id) => `${PATIENT_PHOTOS_KEY_PREFIX}${id}`);
}

// ---------------------------------------------------------------------------
// Small reusable bits
// ---------------------------------------------------------------------------

function SectionHeader({ title, actionLabel, onAction }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-body-medium text-on-surface-var">{title}</h2>
      {actionLabel && (
        <button
          type="button"
          onClick={onAction}
          className="text-body-medium text-primary"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

function InfoRow56({
  icon,
  label,
  value,
  supporting,
  largeIcon = false,
  reserveIconSpace = false,
  variant = "row",
  extraRight,
}) {
  const isCardHeader = variant === "cardHeader";
  const hasValue =
    value !== null &&
    value !== undefined &&
    String(value).trim().length > 0;

  const labelClass = isCardHeader
    ? "text-body-large text-on-surface"
    : "text-body-medium text-on-surface-var";

  const valueClass = hasValue
    ? "text-body-large text-on-surface"
    : "text-body-large text-on-surface-var";

  const renderedValue = hasValue ? value : value;

  return (
    <div className="w-full flex items-center py-[10px] px-4">
      {(icon || reserveIconSpace) && (
        <span className="mr-3 inline-flex h-11 w-11 items-center justify-center">
          {icon ? (
            <img
              src={icon}
              alt=""
              className={largeIcon ? "h-11 w-11" : "h-6 w-6"}
              draggable="false"
            />
          ) : (
            <span className="h-11 w-11" aria-hidden="true" />
          )}
        </span>
      )}

      <div className="flex-1 min-w-0">
        {!!label && <div className={labelClass}>{label}</div>}

        {!isCardHeader && !!renderedValue && (
          <div className={`${valueClass} truncate`}>{renderedValue}</div>
        )}

        {!!supporting && (
          <div className="text-body-medium text-on-surface-var">
            {supporting}
          </div>
        )}
      </div>

      {!!extraRight && (
        <div className="shrink-0 ml-3 pr-8 flex items-center">
          {extraRight}
        </div>
      )}
    </div>
  );
}

function CardDivider() {
  return <Divider variant="outlineVariant" inset className="opacity-[0.35]" />;
}

function SectionDivider() {
  return <Divider variant="outlineVariant" className="my-10 -mx-4 md:mx-0" />;
}

function AppointmentStack({
  items,
  label = "Appointments history",
  onToggleFavorite,
  favoriteReadOnly = false,
  ignoreStoredFavorites = false,
}) {
  if (!items || items.length === 0) return null;

  return (
    <div className="mt-3 min-w-0">
      <div
        role="region"
        aria-label={label}
        tabIndex={0}
        data-testid="appointment-history-carousel"
        className="flex min-w-0 snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain scroll-smooth pb-3 [scrollbar-width:thin]"
      >
        {items.map((appt) => (
          <div
            key={appt.id}
            className="w-[min(360px,calc(100vw-48px))] shrink-0 snap-start"
          >
            <DoctorResultCard
              doctor={appt}
              onToggleFavorite={onToggleFavorite}
              favoriteReadOnly={favoriteReadOnly}
              ignoreStoredFavorites={ignoreStoredFavorites}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- DOB parsing helpers ----------
function parseDobToLocalDate(dob) {
  if (!dob) return null;
  if (dob instanceof Date && !Number.isNaN(dob.getTime())) return dob;

  const raw = textFromUnknown(dob);
  if (!raw) return null;

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const y = Number(iso[1]);
    const m = Number(iso[2]);
    const d = Number(iso[3]);
    return new Date(y, m - 1, d, 12, 0, 0, 0);
  }

  const uk = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (uk) {
    const d = Number(uk[1]);
    const m = Number(uk[2]);
    const y = Number(uk[3]);
    return new Date(y, m - 1, d, 12, 0, 0, 0);
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function calcAgeFromDob(dob) {
  const d = parseDobToLocalDate(dob);
  if (!d) return null;

  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  if (age < 0 || age > 130) return null;
  return age;
}

function pickDependantIcon(d) {
  const relRaw = String(d?.relationship || "").toLowerCase().trim();
  const rel = relRaw.replace(/\s+/g, " ");

  const isParent =
    rel.includes("parent") ||
    rel.includes("mother") ||
    rel.includes("father") ||
    rel.includes("guardian");

  const isPartner =
    rel.includes("spouse") ||
    rel.includes("partner") ||
    rel.includes("wife") ||
    rel.includes("husband");

  const isChildLike =
    rel.startsWith("child") || rel.includes("son") || rel.includes("daughter");

  const age = calcAgeFromDob(d?.date_of_birth || d?.dob);

  if (["sibling", "brother", "sister"].includes(rel)) {
    return age !== null && age <= 14 ? ICON_DEPENDANT_CHILD : ICON_DEPENDANT_ADULT;
  }

  if (isParent || isPartner) return ICON_DEPENDANT_ADULT;

  if (age !== null) return age <= 14 ? ICON_DEPENDANT_CHILD : ICON_DEPENDANT_ADULT;
  if (isChildLike) return ICON_DEPENDANT_CHILD;
  return ICON_DEPENDANT_ADULT;
}

function formatDateDisplay(value, locale = "en-GB") {
  if (!value) return "";
  try {
    const d = parseDobToLocalDate(value) || new Date(value);
    if (Number.isNaN(d.getTime())) return textFromUnknown(value);
    return d.toLocaleDateString(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return textFromUnknown(value);
  }
}

function formatInsuranceSummary(row) {
  if (!row) return "";
  const provider = String(row.provider || row.insurance_provider || "").trim();
  const member = String(
    row.membership_number || row.insurance_member_id || row.insurance_policy_number || ""
  ).trim();
  const plan = String(row.plan_type || row.insurance_plan || "").trim();
  return [provider, member, plan].filter(Boolean).join(" · ");
}

function cleanText(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    const lowered = trimmed.toLowerCase();
    if (
      lowered === "[object object]" ||
      lowered === "{}" ||
      lowered === "[]" ||
      lowered === "null" ||
      lowered === "undefined"
    ) {
      return "";
    }
    return trimmed;
  }
  if (typeof value === "number") return String(value);
  return "";
}

function textFromUnknown(value) {
  if (typeof value === "string") {
    const text = cleanText(value);
    if (!text) return "";
    if (
      (text.startsWith("{") && text.endsWith("}")) ||
      (text.startsWith("[") && text.endsWith("]"))
    ) {
      try {
        return textFromUnknown(JSON.parse(text));
      } catch {
        // leave as plain text
      }
    }
    return text;
  }
  if (Array.isArray(value)) return value.map(textFromUnknown).filter(Boolean).join(", ");
  if (value && typeof value === "object") {
    return (
      cleanText(value.label) ||
      cleanText(value.language) ||
      cleanText(value.name) ||
      cleanText(value.value) ||
      cleanText(value.text) ||
      cleanText(value.title) ||
      cleanText(value.country) ||
      cleanText(value.code) ||
      ""
    );
  }
  return cleanText(value);
}

function isClinicianProfile(profile) {
  const role = textFromUnknown(
    profile?.role || profile?.primary_role || profile?.account_role,
  )
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return role === "doctor" || role === "clinician" || role === "provider";
}

function listFromUnknown(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => textFromUnknown(entry)).filter(Boolean);
  }
  if (value && typeof value === "object") {
    const one = textFromUnknown(value);
    return one ? [one] : [];
  }
  const text = cleanText(value);
  if (!text) return [];
  if (
    (text.startsWith("{") && text.endsWith("}")) ||
    (text.startsWith("[") && text.endsWith("]"))
  ) {
    try {
      return listFromUnknown(JSON.parse(text));
    } catch {
      // fall through to comma split
    }
  }
  return text
    .split(",")
    .map((part) => cleanText(part))
    .filter(Boolean);
}

function boolFromUnknown(value) {
  if (typeof value === "boolean") return value;
  const text = cleanText(value).toLowerCase();
  if (!text) return null;
  if (["yes", "true", "1", "registered"].includes(text)) return true;
  if (["no", "false", "0", "not registered"].includes(text)) return false;
  return null;
}

function formatAppointmentLabel(iso, locale = "en-GB", t = (key, fallback) => fallback) {
  const d = new Date(iso || "");
  if (Number.isNaN(d.getTime())) return t("patient.profile.history.visit", "Visit");
  const day = d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = d.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return t("patient.profile.history.visitAt", "Visit · {day}, {time}", {
    day,
    time,
  });
}

function stringifyConsultType(raw, t = (key, fallback) => fallback) {
  const value = String(raw || "").trim().toLowerCase();
  if (value === "private") return t("patient.profile.consultType.private", "Private consultation");
  if (value === "moh") return t("patient.profile.consultType.moh", "MOH consultation");
  if (value === "evisit" || value === "virtual") return t("patient.profile.consultType.evisit", "Virtual");
  if (!value) return t("patient.profile.consultType.default", "Consultation");
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatSnapshotDateTimeLabel(snapshot, locale, t) {
  const datePart = String(snapshot?.dateISO || snapshot?.selectedDate || "").slice(0, 10);
  const timePart = String(snapshot?.start || snapshot?.selectedTime || "").trim();
  const iso = datePart ? `${datePart}T${timePart || "00:00"}:00` : snapshot?.appointment_date || "";
  return formatAppointmentLabel(iso, locale, t);
}

function mapSnapshotToHistoryCard(snapshot, favoriteDoctorIdSet = null, { t, locale = "en-GB" } = {}) {
  const doctor = snapshot?.doctor || {};
  const facility = snapshot?.facility || doctor?.facility || {};
  const facilityAddress = [facility?.address, facility?.city, facility?.country]
    .filter(Boolean)
    .join(", ");
  const address =
    String(snapshot?.mapAddress || "").trim() ||
    String(doctor?.mapAddress || "").trim() ||
    String(facilityAddress || "").trim() ||
    String(doctor?.address || "").trim() ||
    t?.("patient.profile.fallback.addressNotProvided", "Address not provided") ||
    "Address not provided";
  const hospital =
    String(snapshot?.facilityName || "").trim() ||
    String(facility?.name || "").trim() ||
    String(doctor?.hospital || "").trim() ||
    t?.("patient.profile.fallback.consultationLocation", "Consultation location") ||
    "Consultation location";
  const doctorId =
    doctor?.id ||
    snapshot?.doctorId ||
    snapshot?.doctor_id ||
    doctor?.profile_id ||
    doctor?.doctor_user_id ||
    doctor?.user_id ||
    null;
  const favoriteDoctorIds = buildFavoriteDoctorIds(doctor, {
    doctorId,
    doctor_id: snapshot?.doctor_id || doctor?.doctor_id || doctorId,
    doctor_user_id:
      doctor?.doctor_user_id || doctor?.user_id || snapshot?.doctor_user_id || null,
    user_id:
      doctor?.user_id || doctor?.doctor_user_id || snapshot?.doctor_user_id || null,
    profile_id: doctor?.profile_id || snapshot?.doctor_profile_id || null,
  });

  const favoriteSet =
    favoriteDoctorIdSet instanceof Set ? favoriteDoctorIdSet : null;
  const hydratedFavorite =
    favoriteSet && favoriteDoctorIds.length > 0
      ? favoriteDoctorIds.some((id) => favoriteSet.has(String(id || "").trim()))
      : isFavoriteDoctor(favoriteDoctorIds);

  return {
    id: snapshot?.appointmentId || snapshot?.id || `${doctorId || "doctor"}-${snapshot?.dateISO || ""}`,
    doctorId,
    user_id: doctor?.user_id || doctor?.doctor_user_id || doctorId || null,
    doctor_user_id: doctor?.doctor_user_id || doctor?.user_id || doctorId || null,
    profile_id: doctor?.profile_id || doctor?.profile?.id || null,
    name: resolvePatientFacingDoctorName(doctor, doctor?.name),
    avatar: doctor?.avatar || DOCTOR_AVATAR_FALLBACK,
    specialty:
      String(doctor?.specialty || "").trim() ||
      t?.("patient.profile.fallback.generalPractice", "General practice") ||
      "General practice",
    hospital,
    address,
    yearsExperience: String(doctor?.yearsExperience || "").trim() || "—",
    rating: String(doctor?.rating || "").trim() || "—",
    appointmentLabel:
      String(snapshot?.appointmentLabel || "").trim() ||
      formatSnapshotDateTimeLabel(snapshot, locale, t),
    serviceLabel:
      String(doctor?.serviceLabel || snapshot?.serviceLabel || "").trim() ||
      stringifyConsultType(snapshot?.consultation_type, t),
    serviceLabelPrice:
      String(doctor?.serviceLabelPrice || snapshot?.serviceLabelPrice || snapshot?.serviceNamePrice || "").trim(),
    currency: String(doctor?.currency || snapshot?.currency || "").trim(),
    languages:
      String(doctor?.languages || "").trim() ||
      t?.("patient.profile.fallback.notSpecified", "Not specified") ||
      "Not specified",
    verified_status: normalizeVerificationStatus(
      doctor?.verificationStatus || doctor?.verified_status,
      doctor?.verified ? "verified" : "pending"
    ),
    favoriteIds: favoriteDoctorIds,
    favoriteHydrated: favoriteSet instanceof Set,
    isFavorite:
      snapshot?.isFavorite === true ||
      doctor?.isFavorite === true ||
      hydratedFavorite,
    lat:
      facility?.lat ??
      facility?.latitude ??
      doctor?.lat ??
      doctor?.latitude ??
      null,
    lng:
      facility?.lng ??
      facility?.lon ??
      facility?.longitude ??
      doctor?.lng ??
      doctor?.lon ??
      doctor?.longitude ??
      null,
    facility_id: facility?.id || doctor?.facility_id || null,
    facility: facility || null,
    mapAddress: address,
  };
}

function readNotificationPrefsLocal() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(PATIENT_NOTIFICATIONS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function normalizeNotificationPrefs(raw) {
  const obj = raw && typeof raw === "object" ? raw : {};
  const newsUpdatesOn =
    typeof obj.newsUpdatesOn === "boolean"
      ? obj.newsUpdatesOn
      : typeof obj?.news_updates?.enabled === "boolean"
      ? obj.news_updates.enabled
      : true;
  const localNewsOn =
    typeof obj.localNewsOn === "boolean"
      ? obj.localNewsOn
      : typeof obj?.local_news?.enabled === "boolean"
      ? obj.local_news.enabled
      : false;

  return {
    newsUpdatesOn,
    localNewsOn: newsUpdatesOn ? localNewsOn : false,
  };
}

function notificationChannelsLabel(prefs, t = (key, fallback) => fallback) {
  if (!prefs?.newsUpdatesOn) return t("patient.profile.notifications.off", "Off");
  if (prefs?.localNewsOn) {
    return t(
      "patient.profile.notifications.emailPushLocalNews",
      "Email & Push · Local news & regulations",
    );
  }
  return t("patient.profile.notifications.emailPush", "Email & Push");
}

// ---------------------------------------------------------------------------
// Dev/mock gating (single source of truth for THIS screen)
// ---------------------------------------------------------------------------

function envTrue(v) {
  const s = String(v ?? "").trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "on";
}

function isStagingOrProdBuild() {
  const appEnv = String(import.meta.env.VITE_APP_ENV || "").trim().toLowerCase();
  return appEnv === "staging" || appEnv === "prod" || appEnv === "production";
}

function isDevBypassIdentity(user) {
  return (
    String(user?.id || "").trim() === "dev-user" ||
    user?.app_metadata?.bypass_dev_identity === true ||
    String(user?.user_metadata?.bypass_dev_identity || "").trim() === "true"
  );
}

function allowMocksHere(user) {
  // ✅ Never allow mocks in staging/prod builds
  if (isStagingOrProdBuild()) return false;

  // ✅ Allow on localhost only if you explicitly enabled dev onboarding/bypass
  const localMockEnabled =
    import.meta.env.DEV &&
    (envTrue(import.meta.env.VITE_DEV_ONBOARDING) || envTrue(import.meta.env.VITE_BYPASS_AUTH));

  if (!localMockEnabled) return false;
  return !user?.id || isDevBypassIdentity(user);
}

function readPatientPhotosPreview(profile, authUser, allowMock) {
  try {
    if (!allowMock) return [];
    const raw =
      typeof window !== "undefined"
        ? getPatientPhotoKeys(profile, authUser)
            .map((key) => localStorage.getItem(key))
            .find(Boolean) || null
        : null;
    const parsed = raw ? JSON.parse(raw) : null;

    const arr = Array.isArray(parsed) ? parsed : parsed?.images || [];
    const stored = arr
      .map((it, idx) => ({
        id: it?.id || `p-${idx}`,
        path: typeof it === "object" && it ? it?.path || it?.storage_path || "" : "",
        url:
          typeof it === "string" ? it : it?.url || it?.src || it?.image_url || "",
      }))
      .filter((x) => x.url);

    // ✅ Storage wins always
    if (stored.length) return stored;

    // ✅ Only show demo photos in localhost dev, never on staging/prod
    if (!allowMock) return [];

    return PATIENT_PHOTOS_FALLBACK.map((url, idx) => ({ id: `demo-${idx}`, url }));
  } catch {
    return allowMock
      ? PATIENT_PHOTOS_FALLBACK.map((url, idx) => ({ id: `demo-${idx}`, url }))
      : [];
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PatientProfile({ clinicianContext = null }) {
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const editLabel = t("common.edit", isFrench ? "Modifier" : "Edit");
  const addLabel = t("common.add", isFrench ? "Ajouter" : "Add");
  const localeCode =
    languageCode === "sn"
      ? "sn-ZW"
      : languageCode === "zu"
        ? "zu-ZA"
        : languageCode === "fr"
          ? "fr-FR"
          : "en-GB";
  const { isDark } = useTheme();
  const nav = useNavigate();
  const { profile, user: authUser } = useAuth() || {};
  const location = useLocation();
  const routeState = clinicianContext || location.state || {};

  const ALLOW_MOCK = allowMocksHere(authUser);

  // A clinician may arrive from the appointment shell without router state.
  // Never treat that clinician profile as patient data in that case.
  const isDoctorView =
    Boolean(clinicianContext) || routeState?.from === "doctor" || isClinicianProfile(profile);
  const canEdit = !isDoctorView;
  const authUserFallback = isDoctorView ? null : authUser;

  // Patient data passed from doctor flows
  const hydratedPatient =
    routeState?.patient ||
    routeState?.restored?.patient ||
    routeState?.restored?.patientProfile ||
    null;

  const basePatient = hydratedPatient || (isDoctorView ? null : profile) || null;
  const [dbPatient, setDbPatient] = React.useState(null);
  const patient = dbPatient || basePatient;

  const ehrGranted = Boolean(routeState?.ehrGranted);
  const hasMedicalPreview = Boolean(
    hydratedPatient?.blood_type ||
      hydratedPatient?.bloodType ||
      hydratedPatient?.allergies ||
      hydratedPatient?.medical_conditions ||
      hydratedPatient?.chronic_conditions ||
      hydratedPatient?.organ_donor ||
      hydratedPatient?.is_organ_donor ||
      hydratedPatient?.height_cm ||
      hydratedPatient?.weight_kg ||
      hydratedPatient?.medical?.bloodType ||
      hydratedPatient?.medical?.allergies ||
      hydratedPatient?.medical?.chronicConditions ||
      hydratedPatient?.medical?.organDonor ||
      dbPatient?.blood_type ||
      dbPatient?.allergies ||
      dbPatient?.medical_conditions ||
      dbPatient?.chronic_conditions ||
      dbPatient?.organ_donor ||
      dbPatient?.is_organ_donor ||
      dbPatient?.height_cm ||
      dbPatient?.weight_kg ||
      dbPatient?.medical?.bloodType ||
      dbPatient?.medical?.allergies ||
      dbPatient?.medical?.chronicConditions ||
      dbPatient?.medical?.organDonor
  );
  // Appointment identity alone never grants EHR visibility. Only the existing
  // consent/access decision may reveal medical fields to a clinician.
  const canSeeMedical = !isDoctorView || ehrGranted;
  const canHydratePatientPhotos = canEdit;

  const [signedDbPhotos, setSignedDbPhotos] = React.useState([]);

  React.useEffect(() => {
    let alive = true;

    async function hydratePhotoPathsFromDb() {
      try {
        if (!canHydratePatientPhotos) {
          if (alive) setSignedDbPhotos([]);
          return;
        }

        const paths = Array.isArray(patient?.patient_photo_paths)
          ? patient.patient_photo_paths
              .map((p) => String(p || "").trim())
              .filter(Boolean)
          : [];

        if (!paths.length) {
          if (alive) setSignedDbPhotos([]);
          return;
        }

        const { default: supabase } = await import("@/services/supabaseClient");
        const signedRows = await Promise.all(
          paths.map(async (path, idx) => {
            const { data, error } = await supabase.storage
              .from("patient-photos")
              .createSignedUrl(path, 60 * 60 * 24);

            if (error || !data?.signedUrl) return null;
            return { id: `db-${idx}-${path}`, path, url: data.signedUrl };
          })
        );

        const next = signedRows.filter(Boolean);
        if (!alive) return;
        setSignedDbPhotos(next);

        if (ALLOW_MOCK) {
          try {
            getPatientPhotoKeys(patient, authUser).forEach((key) => {
              localStorage.setItem(key, JSON.stringify(next));
            });
          } catch {
            // non-fatal
          }
        }
      } catch (err) {
        console.error("PatientProfile photo hydrate failed", err);
      }
    }

    hydratePhotoPathsFromDb();
    return () => {
      alive = false;
    };
  }, [
    canHydratePatientPhotos,
    patient?.id,
    patient?.user_id,
    patient?.uid,
    patient?.patient_id,
    patient?.patient_photo_paths,
    ALLOW_MOCK,
    authUser?.id,
  ]);

  const patientPhotos = React.useMemo(() => {
    if (!canHydratePatientPhotos) return [];
    if (Array.isArray(signedDbPhotos) && signedDbPhotos.length) return signedDbPhotos;
    return readPatientPhotosPreview(patient, authUser, ALLOW_MOCK);
  }, [
    canHydratePatientPhotos,
    signedDbPhotos,
    patient?.id,
    patient?.user_id,
    patient?.uid,
    patient?.patient_id,
    authUser?.id,
    ALLOW_MOCK,
  ]);

  const patientPhotoCards = React.useMemo(
    () => (Array.isArray(patientPhotos) ? patientPhotos.slice(0, 6) : []),
    [patientPhotos]
  );

  const [insuranceSummary, setInsuranceSummary] = React.useState("");
  const [insuranceLoaded, setInsuranceLoaded] = React.useState(false);

  const [dependantsRows, setDependantsRows] = React.useState([]);
  const [dependantsLoaded, setDependantsLoaded] = React.useState(false);
  const [appointmentHistoryRows, setAppointmentHistoryRows] = React.useState([]);
  const [appointmentsLoaded, setAppointmentsLoaded] = React.useState(false);
  const [favoriteVersion, setFavoriteVersion] = React.useState(0);

  React.useEffect(
    () => subscribeDoctorFavorites(() => setFavoriteVersion((value) => value + 1)),
    []
  );

  React.useEffect(() => {
    let alive = true;

    async function hydratePatientRow() {
      if (!canEdit) {
        // The appointment-bound clinician route supplies a minimal, server-
        // authorised identity. Do not replace it with an empty cross-user
        // profile query or fetch unconsented medical fields.
        if (clinicianContext) {
          if (alive) setDbPatient(null);
          return;
        }
        const routePatientId = String(routeState?.patientId || "").trim();
        const routePatientUserId = String(routeState?.patientUserId || hydratedPatient?.user_id || "").trim();
        if (!routePatientId && !routePatientUserId) {
          if (alive) setDbPatient(null);
          return;
        }

        try {
          const { default: supabase } = await import("@/services/supabaseClient");
          let patientUserId = routePatientUserId;

          if (!patientUserId && routePatientId) {
            const { data: patientRow } = await supabase
              .from("patients")
              .select("id, user_id")
              .eq("id", routePatientId)
              .maybeSingle();
            patientUserId = String(patientRow?.user_id || "").trim();
          }

          if (!patientUserId) {
            if (alive) setDbPatient(null);
            return;
          }

          const [{ data: profileRow }, { data: authProfileRow }, { data: ehrInfoRow }] = await Promise.all([
            supabase
              .from("patient_profiles")
              .select("*")
              .eq("user_id", patientUserId)
              .maybeSingle(),
            supabase
              .from("profiles")
              .select("user_id, display_name, avatar_url, first_name, last_name")
              .eq("user_id", patientUserId)
              .maybeSingle(),
            supabase
              .from("ehr_patient_info")
              .select("patient_id, date_of_birth, primary_language, height_cm, weight_kg, blood_type, current_medications, allergies, chronic_conditions, is_organ_donor")
              .eq("patient_id", patientUserId)
              .maybeSingle(),
          ]);

          if (!alive) return;
          setDbPatient({
            ...(authProfileRow || {}),
            ...(profileRow || {}),
            date_of_birth: ehrInfoRow?.date_of_birth || profileRow?.date_of_birth || null,
            primary_language: ehrInfoRow?.primary_language || profileRow?.primary_language || null,
            height_cm: ehrInfoRow?.height_cm ?? profileRow?.height_cm ?? null,
            weight_kg: ehrInfoRow?.weight_kg ?? profileRow?.weight_kg ?? null,
            blood_type: ehrInfoRow?.blood_type || profileRow?.blood_type || null,
            current_medications:
              ehrInfoRow?.current_medications ||
              profileRow?.current_medications ||
              profileRow?.medications ||
              null,
            allergies: ehrInfoRow?.allergies || profileRow?.allergies || null,
            chronic_conditions: ehrInfoRow?.chronic_conditions || profileRow?.chronic_conditions || profileRow?.medical_conditions || null,
            organ_donor:
              typeof ehrInfoRow?.is_organ_donor === "boolean"
                ? ehrInfoRow.is_organ_donor
                : profileRow?.organ_donor ?? profileRow?.is_organ_donor ?? null,
            is_organ_donor:
              typeof ehrInfoRow?.is_organ_donor === "boolean"
                ? ehrInfoRow.is_organ_donor
                : profileRow?.is_organ_donor ?? profileRow?.organ_donor ?? null,
            user_id: patientUserId,
          });
        } catch {
          if (alive) setDbPatient(null);
        }
        return;
      }

      if (!authUser?.id) {
        if (alive) setDbPatient(null);
        return;
      }

      try {
        const { default: supabase } = await import("@/services/supabaseClient");
        const [{ data: profileRow, error }, { data: ehrInfoRow }] = await Promise.all([
          supabase
            .from("patient_profiles")
            .select("*")
            .eq("user_id", authUser.id)
            .maybeSingle(),
          supabase
            .from("ehr_patient_info")
            .select("patient_id, date_of_birth, primary_language, height_cm, weight_kg, blood_type, current_medications, allergies, chronic_conditions, is_organ_donor")
            .eq("patient_id", authUser.id)
            .maybeSingle(),
        ]);

        if (!alive) return;
        if (error || (!profileRow && !ehrInfoRow)) return;
        setDbPatient({
          ...(profileRow || {}),
          date_of_birth: ehrInfoRow?.date_of_birth || profileRow?.date_of_birth || null,
          primary_language: ehrInfoRow?.primary_language || profileRow?.primary_language || null,
          height_cm: ehrInfoRow?.height_cm ?? profileRow?.height_cm ?? null,
          weight_kg: ehrInfoRow?.weight_kg ?? profileRow?.weight_kg ?? null,
          blood_type: ehrInfoRow?.blood_type || profileRow?.blood_type || null,
          current_medications:
            ehrInfoRow?.current_medications ||
            profileRow?.current_medications ||
            profileRow?.medications ||
            null,
          allergies: ehrInfoRow?.allergies || profileRow?.allergies || null,
          chronic_conditions: ehrInfoRow?.chronic_conditions || profileRow?.chronic_conditions || profileRow?.medical_conditions || null,
          organ_donor:
            typeof ehrInfoRow?.is_organ_donor === "boolean"
              ? ehrInfoRow.is_organ_donor
              : profileRow?.organ_donor ?? profileRow?.is_organ_donor ?? null,
          is_organ_donor:
            typeof ehrInfoRow?.is_organ_donor === "boolean"
              ? ehrInfoRow.is_organ_donor
              : profileRow?.is_organ_donor ?? profileRow?.organ_donor ?? null,
          user_id: authUser.id,
        });
      } catch {
        // non-fatal fallback to auth.profile/location state
      }
    }

    hydratePatientRow();
    return () => {
      alive = false;
    };
  }, [canEdit, clinicianContext, authUser?.id, hydratedPatient?.user_id, location.key, routeState?.patientId, routeState?.patientUserId]);

  function loadDevInsurance() {
    try {
      const raw = localStorage.getItem(INSURANCE_DEV_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  function loadDevDependants() {
    try {
      const raw = localStorage.getItem(DEPENDANTS_DEV_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  // ✅ BYPASS disabled (keep constant so any existing BYPASS checks still compile)
const BYPASS = false;

  // ------------------------------------------------------------
  // Insurance summary (Profile card)
  // ------------------------------------------------------------
  React.useEffect(() => {
    let alive = true;

    async function loadInsuranceSummary() {
      try {
        if (!canEdit) {
          const summary = formatInsuranceSummary(patient);
          if (alive) setInsuranceSummary(summary);
          return;
        }

        if (ALLOW_MOCK) {
          const devRow = loadDevInsurance();
          const summary = devRow ? formatInsuranceSummary(devRow) : "";
          if (alive) setInsuranceSummary(summary);
          return;
        }

        const { default: supabase } = await import("@/services/supabaseClient");
        const { data: u } = await supabase.auth.getUser();
        const user = u?.user;

        if (!user) {
          if (alive) setInsuranceSummary("");
          return;
        }

        const { data, error } = await supabase
          .from("patient_profiles")
          .select("insurance_provider, insurance_policy_number, insurance_member_id, insurance_plan")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) throw error;

        const summary = data ? formatInsuranceSummary(data) : "";
        if (alive) setInsuranceSummary(summary);
      } catch {
        if (alive) setInsuranceSummary("");
      } finally {
        if (alive) setInsuranceLoaded(true);
      }
    }

    loadInsuranceSummary();
    return () => {
      alive = false;
    };
  }, [
    ALLOW_MOCK,
    canEdit,
    patient?.insurance_provider,
    patient?.insurance_policy_number,
    patient?.insurance_member_id,
    patient?.insurance_plan,
  ]);

  // ------------------------------------------------------------
  // Dependants (rows)
  // ------------------------------------------------------------
  React.useEffect(() => {
    let alive = true;

    async function loadDependantsRows() {
      try {
        setDependantsLoaded(false);

        if (!canEdit) {
          let routePatientUserId = String(
            patient?.user_id || routeState?.patientUserId || hydratedPatient?.user_id || ""
          ).trim();
          const routePatientId = String(
            routeState?.patientId ||
              patient?.id ||
              hydratedPatient?.patient_id ||
              hydratedPatient?.id ||
              ""
          ).trim();

          const { default: supabase } = await import("@/services/supabaseClient");

          if (!routePatientUserId && routePatientId) {
            const [{ data: patientRow }, { data: patientProfileRow }, { data: authProfileRow }] =
              await Promise.all([
                supabase
                  .from("patients")
                  .select("id, user_id")
                  .eq("id", routePatientId)
                  .maybeSingle(),
                supabase
                  .from("patient_profiles")
                  .select("id, user_id")
                  .eq("id", routePatientId)
                  .maybeSingle(),
                supabase
                  .from("profiles")
                  .select("id, user_id")
                  .eq("id", routePatientId)
                  .maybeSingle(),
              ]);

            routePatientUserId = String(
              patientRow?.user_id ||
                patientProfileRow?.user_id ||
                authProfileRow?.user_id ||
                ""
            ).trim();
          }

          if (!routePatientUserId) {
            if (alive) setDependantsRows([]);
            return;
          }

          const { data, error } = await supabase
            .from("patient_dependants")
            .select("id, first_name, last_name, verification_status, date_of_birth, relationship, avatar_path, avatar_url, created_at")
            .eq("user_id", routePatientUserId)
            .order("created_at", { ascending: true });

          if (error) throw error;

          if (!alive) return;
          setDependantsRows(Array.isArray(data) ? data : []);
          return;
        }

        if (ALLOW_MOCK) {
          const list = loadDevDependants();

          const norm = (Array.isArray(list) ? list : []).map((d) => {
            const first = (d?.first_name ?? d?.firstName ?? "").toString();
            const last = (d?.last_name ?? d?.lastName ?? "").toString();
            const statusRaw = (d?.verification_status ?? d?.verificationStatus ?? "")
              .toString()
              .toLowerCase();

            const verification_status =
              statusRaw === "verified"
                ? "verified"
                : statusRaw === "rejected"
                ? "rejected"
                : statusRaw === "pending" || statusRaw === "pending_review"
                ? "pending_review"
                : "unverified";

            return {
              id: d?.id ?? d?._id ?? `${first}-${last}-${Math.random()}`,
              first_name: first,
              last_name: last,
              verification_status,
              date_of_birth: d?.date_of_birth ?? d?.dateOfBirth ?? "",
              relationship: d?.relationship ?? "",
            };
          });

          if (!alive) return;
          setDependantsRows(norm);
          return;
        }

        const { default: supabase } = await import("@/services/supabaseClient");
        const { data: u } = await supabase.auth.getUser();
        const user = u?.user;

        if (!user) {
          if (alive) setDependantsRows([]);
          return;
        }

        const { data, error } = await supabase
          .from("patient_dependants")
          .select("id, first_name, last_name, verification_status, date_of_birth, relationship, avatar_path, avatar_url, created_at")
          .eq("user_id", user.id)
          .order("created_at", { ascending: true });

        if (error) throw error;

        const normalizedRows = (Array.isArray(data) ? data : []).map((d) => {
          const statusRaw = String(d?.verification_status || "").toLowerCase();
          const verification_status =
            statusRaw === "verified"
              ? "verified"
              : statusRaw === "rejected"
              ? "rejected"
              : statusRaw === "pending" || statusRaw === "pending_review"
              ? "pending_review"
              : "unverified";
          return { ...d, verification_status };
        });
        const rows = await hydrateDependantIdentityStatuses(supabase, normalizedRows);

        if (!alive) return;
        setDependantsRows(rows);
      } catch {
        if (alive) setDependantsRows([]);
      } finally {
        if (alive) setDependantsLoaded(true);
      }
    }

    loadDependantsRows();

    if (routeState?.dependantsUpdated) {
      nav(location.pathname, { replace: true, state: null });
    }

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.key,
    routeState?.dependantsUpdated,
    routeState?.patientUserId,
    hydratedPatient?.user_id,
    patient?.user_id,
    ALLOW_MOCK,
  ]);

  // ------------------------------------------------------------
  // Appointments history (DB-backed)
  // ------------------------------------------------------------
  React.useEffect(() => {
    let alive = true;

    async function loadAppointmentHistoryRows() {
      try {
        setAppointmentsLoaded(false);

        if (ALLOW_MOCK) {
          if (alive) setAppointmentHistoryRows([]);
          return;
        }

        const routePatientId = String(
          routeState?.patientId ||
            routeState?.restored?.patient_row_id ||
            routeState?.restored?.patient?.patient_id ||
            hydratedPatient?.patient_id ||
            ""
        ).trim();
        const routePatientUserId = String(
          routeState?.patientUserId ||
            routeState?.restored?.patient_user_id ||
            routeState?.restored?.patient?.user_id ||
            hydratedPatient?.user_id ||
            patient?.user_id ||
            ""
        ).trim();

        const snapshots = canEdit
          ? await listMyPastAppointmentSnapshots()
          : await listPastAppointmentSnapshotsForPatient({
              patientId: routePatientId,
              patientUserId: routePatientUserId,
            });
        const patientFavoriteIds =
          !canEdit && routePatientUserId
            ? Array.from(
                new Set([
                  ...getFavoriteDoctorIdsForUser(routePatientUserId),
                  ...(await listFavoriteDoctorIdsForPatient(routePatientUserId)),
                ])
              )
            : [];
        const patientFavoriteIdSet = new Set(
          patientFavoriteIds.map((id) => String(id || "").trim()).filter(Boolean)
        );

        const routeDoctor = routeState?.restored?.doctor || null;
        const routeAppointmentId = String(routeState?.appointmentId || "").trim();
        const routeDoctorFavoriteIds = buildFavoriteDoctorIds(routeDoctor, {
          doctorId:
            routeDoctor?.doctorId ||
            routeDoctor?.doctor_id ||
            routeDoctor?.id ||
            routeDoctor?.profile_id ||
            routeDoctor?.doctor_user_id ||
            routeDoctor?.user_id ||
            null,
          doctor_id:
            routeDoctor?.doctor_id ||
            routeDoctor?.doctorId ||
            routeDoctor?.id ||
            routeDoctor?.profile_id ||
            routeDoctor?.doctor_user_id ||
            routeDoctor?.user_id ||
            null,
          doctor_user_id:
            routeDoctor?.doctor_user_id ||
            routeDoctor?.user_id ||
            routeDoctor?.id ||
            null,
          user_id:
            routeDoctor?.user_id ||
            routeDoctor?.doctor_user_id ||
            routeDoctor?.id ||
            null,
          profile_id: routeDoctor?.profile_id || routeDoctor?.id || null,
        });
        const routeDoctorFavoriteIdSet = new Set(
          routeDoctorFavoriteIds.map((id) => String(id || "").trim()).filter(Boolean)
        );

        const mapped = (Array.isArray(snapshots) ? snapshots : [])
          .map((snapshot) =>
            mapSnapshotToHistoryCard(snapshot, !canEdit && patientFavoriteIdSet.size > 0 ? patientFavoriteIdSet : null, {
              t,
              locale: languageCode,
            })
          )
          .map((row) => {
            const rowFavoriteIds = buildFavoriteDoctorIds(row, {
              doctorId:
                row?.doctorId ||
                row?.doctor_id ||
                row?.profile_id ||
                row?.doctor_user_id ||
                row?.user_id ||
                null,
              doctor_id: row?.doctor_id || row?.doctorId || row?.profile_id || null,
              doctor_user_id:
                row?.doctor_user_id || row?.user_id || row?.doctorId || row?.profile_id || null,
              user_id:
                row?.user_id || row?.doctor_user_id || row?.doctorId || row?.profile_id || null,
              profile_id: row?.profile_id || row?.doctorId || row?.doctor_id || null,
            });
            const matchesRouteAppointment =
              routeAppointmentId && String(row?.id || "").trim() === routeAppointmentId;
            const matchesRouteDoctor =
              routeDoctorFavoriteIdSet.size > 0 &&
              rowFavoriteIds.some((id) =>
                routeDoctorFavoriteIdSet.has(String(id || "").trim())
              );

            if (
              !isDoctorView ||
              !routeDoctor ||
              (!matchesRouteAppointment && !matchesRouteDoctor)
            ) {
              return row;
            }

            return {
              ...row,
              doctorId:
                row?.doctorId ||
                routeDoctor?.doctorId ||
                routeDoctor?.doctor_id ||
                routeDoctor?.user_id ||
                routeDoctor?.doctor_user_id ||
                routeDoctor?.id ||
                null,
              user_id:
                row?.user_id ||
                routeDoctor?.user_id ||
                routeDoctor?.doctor_user_id ||
                null,
              doctor_user_id:
                row?.doctor_user_id ||
                routeDoctor?.doctor_user_id ||
                routeDoctor?.user_id ||
                null,
              profile_id:
                row?.profile_id ||
                routeDoctor?.profile_id ||
                routeDoctor?.id ||
                null,
              doctor_profile_id:
                row?.doctor_profile_id ||
                row?.profile_id ||
                routeDoctor?.profile_id ||
                routeDoctor?.id ||
                null,
              favoriteIds:
                Array.isArray(row?.favoriteIds) && row.favoriteIds.length > 0
                  ? row.favoriteIds
                  : rowFavoriteIds,
              favoriteHydrated:
                row?.favoriteHydrated === true || patientFavoriteIdSet.size > 0,
              isFavorite:
                row?.isFavorite === true ||
                routeDoctor?.isFavorite === true,
            };
          });

        if (!alive) return;
        setAppointmentHistoryRows(mapped);
      } catch (err) {
        console.error("PatientProfile appointments history load failed", err);
        if (alive) setAppointmentHistoryRows([]);
      } finally {
        if (alive) setAppointmentsLoaded(true);
      }
    }

    loadAppointmentHistoryRows();
    return () => {
      alive = false;
    };
  }, [
    ALLOW_MOCK,
    canEdit,
    authUser?.id,
    favoriteVersion,
    hydratedPatient?.patient_id,
    hydratedPatient?.user_id,
    isDoctorView,
    location.key,
    routeState?.appointmentId,
    routeState?.patientId,
    routeState?.patientUserId,
    routeState?.restored?.doctor,
  ]);

  const DEV_PHOTO_KEY = "afrimed:dev:patient_profile_photo";
  const devPhoto = ALLOW_MOCK && canEdit ? localStorage.getItem(DEV_PHOTO_KEY) : null;
  const cachedProfileAvatar = React.useMemo(
    () => (canEdit ? readAvatarSessionCache({ role: "patient", userId: authUser?.id }) : null),
    [authUser?.id, canEdit]
  );
  // A clinician may inspect a patient profile, but their own authenticated
  // avatar must never be used as that patient's temporary fallback.
  const authProfileAvatar = canEdit
    ? String(profile?.profile_image_url || profile?.avatar_url || "").trim()
    : "";

  const [photoUrl, setPhotoUrl] = React.useState(
    devPhoto || cachedProfileAvatar || authProfileAvatar || "/icons/Profile_Avatar_borderless.svg"
  );

  React.useEffect(() => {
    let alive = true;

    async function loadAvatar() {
      // BYPASS: dev only
      if (BYPASS) {
        if (alive) setPhotoUrl(devPhoto || "/icons/Profile_Avatar_borderless.svg");
        return;
      }

      let path =
        patient?.profile_photo_path ||
        patient?.profile_photo_storage_path ||
        patient?.photo_path ||
        patient?.avatar_path ||
        "";

      let fallbackImageUrl =
        patient?.profile_image_url || patient?.avatar_url || (canEdit ? authProfileAvatar : "") || "";

      const immediatelyUsableAvatar = devPhoto || cachedProfileAvatar || fallbackImageUrl;
      if (immediatelyUsableAvatar && alive) {
        setPhotoUrl(immediatelyUsableAvatar);
      }

      if (!path && authUser?.id && canEdit) {
        try {
          const { default: supabase } = await import("@/services/supabaseClient");
          const { data: fresh } = await supabase
            .from("patient_profiles")
            .select("*")
            .eq("user_id", authUser.id)
            .maybeSingle();

          path = String(
            fresh?.profile_photo_path ||
              fresh?.profile_photo_storage_path ||
              fresh?.photo_path ||
              fresh?.avatar_path ||
              ""
          ).trim();
          fallbackImageUrl = String(
            fresh?.profile_image_url || fresh?.avatar_url || fallbackImageUrl || ""
          ).trim();
        } catch {
          // non-fatal; fallback below
        }
      }

      if (!path) {
        if (alive) {
          const next = fallbackImageUrl || "/icons/Profile_Avatar_borderless.svg";
          setPhotoUrl(next);
          if (fallbackImageUrl && canEdit) {
            writeAvatarSessionCache({ role: "patient", userId: authUser?.id, src: fallbackImageUrl });
          }
        }
        return;
      }

      try {
        const { default: supabase } = await import("@/services/supabaseClient");
        const storage = supabase.storage.from(PATIENT_BUCKET);
        const { data, error } = await storage.createSignedUrl(path, 60 * 60 * 24);

        if (!error && data?.signedUrl && alive) {
          setPhotoUrl(data.signedUrl);
          if (canEdit) {
            writeAvatarSessionCache({ role: "patient", userId: authUser?.id, src: data.signedUrl });
          }
          return;
        }

        const { data: publicData } = storage.getPublicUrl(path);
        const publicUrl = publicData?.publicUrl || "";
        if (publicUrl && alive) {
          setPhotoUrl(publicUrl);
          if (canEdit) {
            writeAvatarSessionCache({ role: "patient", userId: authUser?.id, src: publicUrl });
          }
          return;
        }

        if (alive) {
          setPhotoUrl(fallbackImageUrl || "/icons/Profile_Avatar_borderless.svg");
        }
      } catch {
        if (alive) {
          setPhotoUrl(fallbackImageUrl || "/icons/Profile_Avatar_borderless.svg");
        }
      }
    }

    loadAvatar();
    return () => {
      alive = false;
    };
  }, [
    BYPASS,
    authProfileAvatar,
    devPhoto,
    cachedProfileAvatar,
    patient?.profile_photo_path,
    patient?.profile_photo_storage_path,
    patient?.photo_path,
    patient?.avatar_path,
    patient?.profile_image_url,
    patient?.avatar_url,
    authUser?.id,
    canEdit,
  ]);

  // -------------------------------------------------------------------------
  // IMPORTANT: blueprint/mock data must NEVER leak to staging/prod
  // -------------------------------------------------------------------------
  const blueprintPatient = React.useMemo(() => {
    if (!ALLOW_MOCK) {
      return {
        appointments: [],
        contact: { phone: "", email: "", nationalId: "" },
        address: { street: "", postalCode: "", country: "", distance: "" },
        medical: { bloodType: "", allergies: "", chronicConditions: "", organDonor: "", heightWeight: "" },
        languages: "",
        notifications: { title: "", channels: "" },
        dobLabel: "",
        name: "",
      };
    }

    // ✅ Localhost-only demo (keeps rails from looking empty while building)
    return {
      name: "Boitumelo Dube",
      dobLabel: formatDateDisplay("1990-03-24", localeCode),
      appointments: [
        {
          id: "appt-1",
          doctorName: "Dr. Kealeboga Morapedi",
          doctorAvatar: "/assets/images/Dr-256.jpg",
          specialty: t(
            "patient.profile.mock.specialtyDermatologist",
            "Dermatologist",
          ),
          hospital: "Life Gaborone Private Hospital",
          address: "8448 Segoditshane Way, Gaborone, Botswana",
          yearsExperience: t("patient.profile.mock.yearsExperience", "14 years"),
          rating: "4.8",
          serviceLabel: t("patient.profile.mock.serviceLabel", "Service"),
          serviceName: t(
            "patient.profile.mock.serviceNameFirstConsultancy",
            "First time consultancy",
          ),
          price: "100.00",
          currency: "BWP",
          languages: t(
            "patient.profile.mock.languagesEnglishTswana",
            "English, Tswana",
          ),
          verified: "/icons/doctor/verified.svg",
        },
      ],
      contact: {
        phone: "+267 72 987 654",
        email: "boitumelo@gmail.com",
        nationalId: "KJ1234",
      },
      address: {
        street: "4775 Notwane Rd, Gaborone",
        postalCode: "00267",
        country: "Botswana",
        distance: t(
          "patient.profile.mock.distanceFromCityCentre",
          "5.2 km from city centre",
        ),
      },
      medical: {
        bloodType: "O+",
        allergies: "Penicillin, Nuts",
        chronicConditions: t(
          "patient.profile.mock.chronicConditionsHypertensionDiabetes",
          "Hypertension, Diabetes",
        ),
        organDonor: "Yes",
      },
      languages: t(
        "patient.profile.mock.languagesEnglishTswana",
        "English, Tswana",
      ),
      notifications: {
        title: "",
        channels: "",
      },
    };
  }, [ALLOW_MOCK, localeCode, t]);
  const fallbackPatient = isDoctorView
    ? {
        appointments: [],
        contact: { phone: "", email: "", nationalId: "" },
        address: { street: "", postalCode: "", country: "", distance: "" },
        medical: { bloodType: "", allergies: "", chronicConditions: "", organDonor: "", heightWeight: "" },
        languages: "",
        notifications: { title: "", channels: "" },
        dobLabel: "",
        name: "",
      }
    : blueprintPatient;

  // -------------------------------------------------------------------------
  // Start wiring EditIntro → Profile with graceful fallback
  // -------------------------------------------------------------------------

  const introPreview = isDoctorView || !ALLOW_MOCK ? {} : loadPatientIntroPreview() || {};
  const routedUiPatient = isDoctorView ? null : routeState?.uiPatient || null;

  const fullNameFromPatient =
    textFromUnknown(patient?.full_name) ||
    [textFromUnknown(patient?.first_name || patient?.given_name || ""), textFromUnknown(patient?.last_name || patient?.family_name || "")]
      .filter(Boolean)
      .join(" ") ||
    textFromUnknown(patient?.name) ||
    "";
  const patientSalutation = (() => {
    const raw = textFromUnknown(patient?.salutation || introPreview?.salutation);
    return ["Mr.", "Mrs.", "Ms.", "Miss", "Mme.", "Prof."].includes(raw) ? raw : "";
  })();
  const patientFamilyName =
    textFromUnknown(patient?.last_name || patient?.family_name || introPreview?.lastName) ||
    (() => {
      const parts = textFromUnknown(fullNameFromPatient).split(/\s+/).filter(Boolean);
      return parts.length > 1 ? parts.slice(-1)[0] : "";
    })();
  const addressedPatientName =
    patientSalutation && patientFamilyName
      ? `${patientSalutation} ${patientFamilyName}`.trim()
      : "";

  const dobFromPatient = textFromUnknown(patient?.date_of_birth || patient?.dob || patient?.birthdate || "");
  const phoneFromPatient =
    textFromUnknown(patient?.phone || patient?.phone_number || patient?.mobile || authUserFallback?.phone || "");
  const emailFromPatient = textFromUnknown(patient?.email || patient?.email_address || authUserFallback?.email || "");
  const nationalIdFromPatient =
    textFromUnknown(patient?.national_id_number || patient?.national_id || patient?.id_number || "");

  const previewName = textFromUnknown(introPreview.name);
  const name =
    addressedPatientName ||
    previewName ||
    textFromUnknown(fullNameFromPatient) ||
    textFromUnknown(fallbackPatient.name) ||
    "";

  const previewDobIso = textFromUnknown(introPreview.dobIso);
  const dobFromPreview = previewDobIso ? formatDateDisplay(previewDobIso, localeCode) : "";
  const dobFromDb = dobFromPatient ? formatDateDisplay(dobFromPatient, localeCode) : "";

  const dobLabel = dobFromPreview || dobFromDb || fallbackPatient.dobLabel || "";
  // The profile's governed patient row is already hydrated for this route. Do
  // not derive a care-facing reference from route, auth, or relationship IDs.
  const patientReference = textFromUnknown(patient?.patient_reference);

  const previewPhone = textFromUnknown(introPreview.phone);
  const previewEmail = textFromUnknown(introPreview.email);
  const previewNationalId = textFromUnknown(introPreview.nationalId);

  const phone = previewPhone || phoneFromPatient || fallbackPatient.contact.phone || "";
  const email = previewEmail || emailFromPatient || fallbackPatient.contact.email || "";
  const nationalId = previewNationalId || nationalIdFromPatient || fallbackPatient.contact.nationalId || "";
  const hasCanonicalNationalId = Boolean(String(nationalId || "").trim());

  const phoneIsPlaceholder = !String(phone || "").trim();
  const emailIsPlaceholder = !String(email || "").trim();

  const phoneVerified = Boolean(
    introPreview?.phoneVerified ||
      introPreview?.phone_verified ||
      patient?.phone_verified ||
      patient?.is_phone_verified ||
      patient?.phone_verified_at ||
      patient?.phone_confirmed_at ||
      authUserFallback?.phone_confirmed_at ||
      routedUiPatient?.phone_verified ||
      routedUiPatient?.contact?.phoneVerified ||
      routedUiPatient?.contact?.phone_verified
  );

  const emailVerified = Boolean(
    introPreview?.emailVerified ||
      introPreview?.email_verified ||
      patient?.email_verified ||
      patient?.is_email_verified ||
      patient?.email_verified_at ||
      patient?.email_confirmed_at ||
      authUserFallback?.email_confirmed_at ||
      routedUiPatient?.email_verified ||
      routedUiPatient?.contact?.emailVerified ||
      routedUiPatient?.contact?.email_verified
  );

  const showPhoneVerified = phoneVerified && !phoneIsPlaceholder;
  const showPhonePending = !phoneVerified && !phoneIsPlaceholder;
  const showEmailVerified = emailVerified && !emailIsPlaceholder;

  const previewStreetLine1 = textFromUnknown(introPreview.addressLine || introPreview.addressStreet || "");
  const previewStreetLine2 = textFromUnknown(introPreview.addressLine2 || introPreview.city || "");
  const previewStreet = [previewStreetLine1, previewStreetLine2].filter(Boolean).join(", ");
  const previewPostal = textFromUnknown(introPreview.postalCode || introPreview.addressPostalCode || "");
  const previewCountry = textFromUnknown(introPreview.country || introPreview.addressCountry || "");

  const previewBlood = textFromUnknown(introPreview.bloodType);

  const previewAllergies = listFromUnknown(introPreview.allergies).join(", ");

  const previewConditions = listFromUnknown(introPreview.conditions).join(", ");

  const previewDonor = textFromUnknown(introPreview.organDonor);

  const dbStreetLine1 = textFromUnknown(
    patient?.street_address || patient?.street || patient?.address_street || patient?.address?.street || ""
  );
  const dbStreetLine2 = textFromUnknown(patient?.city || patient?.addressLine2 || patient?.address?.city || "");
  const dbStreet = [dbStreetLine1, dbStreetLine2].filter(Boolean).join(", ");

  const street = dbStreet || previewStreet || fallbackPatient.address.street || "";

  const postalCode =
    patient?.postal_code ||
    patient?.postalCode ||
    patient?.address?.postalCode ||
    previewPostal ||
    fallbackPatient.address.postalCode ||
    "";

  const country =
    patient?.country ||
    patient?.address?.country ||
    previewCountry ||
    fallbackPatient.address.country ||
    "";

  const distance =
    patient?.distance ||
    patient?.address?.distance ||
    fallbackPatient.address.distance ||
    "";

  const bloodTypeFromDb = textFromUnknown(
    patient?.blood_type || patient?.bloodType || patient?.medical?.bloodType || ""
  );

  const bloodType =
    previewBlood ||
    bloodTypeFromDb ||
    fallbackPatient.medical.bloodType ||
    "";

  const allergiesFromDb = listFromUnknown(patient?.allergies || patient?.medical?.allergies).join(", ");

  const allergies =
    previewAllergies ||
    allergiesFromDb ||
    fallbackPatient.medical.allergies ||
    "";

  const medicalConditionsFromDb = listFromUnknown(
    patient?.medical_conditions ||
      patient?.chronic_conditions ||
      patient?.chronicConditions ||
      patient?.medical_history ||
      patient?.medical?.chronicConditions
  ).join(", ");

  const chronicConditions =
    previewConditions ||
    medicalConditionsFromDb ||
    fallbackPatient.medical.chronicConditions ||
    "";

  const medications = listFromUnknown(
    patient?.current_medications ||
      patient?.medications ||
      patient?.medical?.currentMedications ||
      patient?.medical?.medications
  ).join(", ");

  const emergencyAlerts = listFromUnknown(
    patient?.emergency_alerts ||
      patient?.emergencyAlerts ||
      patient?.emergency_notes ||
      patient?.medical?.emergencyAlerts
  ).join(", ");

  const donorFromDbBool = boolFromUnknown(
    patient?.organ_donor ??
      patient?.is_organ_donor ??
      patient?.organDonor ??
      patient?.medical?.organDonor
  );
  const donorFromDb =
    donorFromDbBool === null
      ? ""
      : donorFromDbBool
        ? t("common.yes", "Yes")
        : t("common.no", "No");

  const organDonor =
    previewDonor ||
    donorFromDb ||
    fallbackPatient.medical.organDonor ||
    "";

  const previewHeight = textFromUnknown(introPreview.heightCm || introPreview.height_cm);
  const previewWeight = textFromUnknown(introPreview.weightKg || introPreview.weight_kg);
  const heightFromDb = textFromUnknown(patient?.height_cm || patient?.height);
  const weightFromDb = textFromUnknown(patient?.weight_kg || patient?.weight);
  const heightWeight =
    previewHeight && previewWeight
      ? `${previewHeight} cm • ${previewWeight} kg`
      : previewHeight
        ? `${previewHeight} cm`
        : previewWeight
          ? `${previewWeight} kg`
          : heightFromDb && weightFromDb
            ? `${heightFromDb} cm • ${weightFromDb} kg`
            : heightFromDb
              ? `${heightFromDb} cm`
              : weightFromDb
                ? `${weightFromDb} kg`
                : fallbackPatient.medical.heightWeight || "";

  const orderLanguages = (items, preferred) => {
    const preferredClean = String(preferred || "").trim();
    const values = [
      ...(preferredClean ? [preferredClean] : []),
      ...listFromUnknown(items),
    ];
    const deduped = Array.from(
      new Map(
        values
          .map((item) => String(item || "").trim())
          .filter(Boolean)
          .map((item) => [item.toLowerCase(), item])
      ).values()
    );
    return preferredClean
      ? [
          preferredClean,
          ...deduped.filter((item) => item.toLowerCase() !== preferredClean.toLowerCase()),
        ]
      : deduped;
  };
  const previewLanguagesList = orderLanguages(
    introPreview.languages,
    introPreview.primaryLanguage || introPreview.primary_language,
  );
  const dbLanguagesList = orderLanguages(
    patient?.languages || patient?.language || patient?.medical?.languages,
    patient?.primary_language,
  );
  const fallbackLanguagesList = orderLanguages(fallbackPatient.languages, "");
  const languagesList =
    previewLanguagesList.length > 0
      ? previewLanguagesList
      : dbLanguagesList.length > 0
        ? dbLanguagesList
        : fallbackLanguagesList;
  const preferredLanguage = languagesList[0] || "";
  const otherLanguages = languagesList.slice(1);
  const languagesText = languagesList.join(", ");
  const localNotificationPrefs = isDoctorView ? {} : readNotificationPrefsLocal();
  const notificationPrefs = normalizeNotificationPrefs(
    patient?.notification_prefs || localNotificationPrefs
  );
  const notificationsChannels = notificationChannelsLabel(notificationPrefs, t);
  const appointmentCards =
    appointmentHistoryRows.length > 0
      ? appointmentHistoryRows
      : !isDoctorView && ALLOW_MOCK
      ? fallbackPatient.appointments
      : [];

  const uiPatient = {
    name,
    dobLabel,
    appointments: appointmentCards,
    contact: { phone, email, nationalId },
    address: { street, postalCode, country, distance },
    medical: { bloodType, allergies, chronicConditions, medications, emergencyAlerts, organDonor, heightWeight },
    languages: languagesText,
    preferredLanguage,
    otherLanguages,
    notifications: {
      title: t("patient.profile.notifications.title", "News & Updates"),
      channels: notificationsChannels || fallbackPatient.notifications.channels || "",
    },
  };

  const withPlaceholder = (value, placeholder) =>
    value && String(value).trim().length > 0 ? value : placeholder;

  const rawIdStatus =
    patient?.id_verification_status ??
    patient?.verification_status ??
    uiPatient?.id_verification_status ??
    uiPatient?.contact?.idVerificationStatus ??
    null;

  const idStatus = normalizeVerificationStatus(rawIdStatus, "unverified");

  const idVerifiedLocal = !isDoctorView && Boolean(introPreview?.idVerified);
  const idPendingLocal = !isDoctorView && Boolean(introPreview?.idPending);

  const idIsVerified = hasCanonicalNationalId && (idVerifiedLocal || idStatus === "verified");
  const idIsPending =
    hasCanonicalNationalId && !idIsVerified && (idPendingLocal || idStatus === "pending");
  // -------------------------------------------------------------------------
  // UI
  // -------------------------------------------------------------------------
  return (
    <div className="profile-page-shell w-full">
      <div className="relative profile-cover-bleed -mt-[16px] md:-mt-[20px]" data-testid="patient-profile-cover">
        <CoverPhoto
          src={isDark ? COVER_IMAGE_DARK : COVER_IMAGE_LIGHT}
          heightClassName="h-[172px] sm:h-[172px] md:h-[280px] lg:h-[320px]"
          overlayClassName={
            isDark
              ? "bg-[linear-gradient(180deg,rgba(8,10,18,0.06)_0%,rgba(8,10,18,0.14)_38%,rgba(8,10,18,0.24)_68%,rgba(8,10,18,0.34)_100%)]"
              : ""
          }
        />

        <div className="absolute inset-x-0 top-0">
          <div className="afrimed-rail afrimed-rail--comfortable pt-2">
            <TopBarProfile
              variant="patient"
              onBack={() => nav(-1)}
              onAttach={canEdit ? () => nav("/settings/patient/p/ehr") : undefined}
              onCalendar={canEdit ? () => nav("/appointments") : undefined}
              onRightAction={canEdit ? () => nav("/settings/patient/p/edit-intro") : undefined}
              className="mt-16 md:mt-20 lg:mt-24"
            />
          </div>
        </div>

        <AvatarHeaderProfile
          src={photoUrl}
          mobileOffsetClassName="pt-[132px] sm:pt-[132px]"
          dataTestId="patient-profile-avatar"
        />
      </div>

      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-[calc(128px+env(safe-area-inset-bottom))] md:pb-24">
        <div
          data-testid="patient-profile-identity"
          className="mt-[44px] md:mt-[102px] lg:mt-[86px] flex items-start justify-between"
        >
          <div>
            <h1 className="text-title-large text-on-surface">
              {uiPatient.name || t("patient.profile.notProvided", "Not provided")}
            </h1>
            {patientReference ? (
              <p className="mt-1 text-body-small text-on-surface-var">
                {isFrench
                  ? `${t("patient.profile.identity.patientId", "Identifiant Patient")} : ${patientReference}`
                  : `${t("patient.profile.identity.patientId", "Patient ID")}: ${patientReference}`}
              </p>
            ) : null}
            <p className="mt-1 text-body-large text-on-surface-var">
              {uiPatient.dobLabel || t("patient.profile.notProvided", "Not provided")}
            </p>
          </div>

          {canEdit && (
            <button
              type="button"
              className="mt-1 text-body-medium text-primary"
              onClick={() => nav("/settings/patient/p/edit-intro")}
            >
              {editLabel}
            </button>
          )}
        </div>

        {canEdit ? (
          <FolaOnboardingCard
            role="patient"
            placement="patientProfile"
            className="mt-6 md:ml-[220px]"
          />
        ) : null}

        <div className="mt-8">
          <SectionHeader
            title={t("patient.profile.sections.appointments", "Appointments history")}
            actionLabel={canEdit ? t("common.all", "All") : null}
            onAction={canEdit ? () => {} : undefined}
          />

          <AppointmentStack
            items={uiPatient.appointments}
            label={t("patient.profile.sections.appointments", "Appointments history")}
            favoriteReadOnly={isDoctorView}
            ignoreStoredFavorites={isDoctorView}
            onToggleFavorite={
              canEdit
                ? (doctorId, nextValue) => {
                    setAppointmentHistoryRows((current) =>
                      current.map((row) =>
                        String(row?.id || "") === String(doctorId || "") ||
                        String(row?.doctorId || "") === String(doctorId || "")
                          ? { ...row, isFavorite: nextValue }
                          : row
                      )
                    );
                  }
                : undefined
            }
          />

          {/* ✅ Staging/prod friendly empty state */}
          {appointmentsLoaded && (!uiPatient.appointments || uiPatient.appointments.length === 0) && (
            <Card className="mt-3 p-4 text-body-medium text-on-surface-var">
              {t("patient.profile.empty.appointments", "No appointments yet.")}
            </Card>
          )}
        </div>

        {!isDoctorView ? (
          <>
            <SectionDivider />

            {/* Contact data */}
            <div>
          <SectionHeader
            title={t("patient.profile.sections.contact", "Contact data")}
            actionLabel={canEdit ? editLabel : null}
            onAction={canEdit ? () => nav("/settings/patient/p/edit-phone") : undefined}
          />

          <Card className="mt-2 overflow-hidden">
            <InfoRow56
              icon={ICON_CONTACT_SECTION}
              label={t("patient.profile.sections.contact", "Contact data")}
              value={null}
              largeIcon
              variant="cardHeader"
            />

            <CardDivider />

            <InfoRow56
              icon={ICON_PHONE}
              label={t("patient.profile.labels.phone", "Phone")}
              value={withPlaceholder(
                uiPatient.contact.phone,
                t("patient.profile.placeholders.phone", "Your phone number"),
              )}
              extraRight={
                showPhoneVerified ? (
                  <img
                    src={ICON_VERIFIED_CHIP}
                    alt={t("patient.profile.verified", "Verified")}
                    className="h-5 w-auto"
                    draggable="false"
                  />
                ) : showPhonePending ? (
                  <img
                    src={ICON_PENDING_VERIFICATION}
                    alt={t("patient.profile.pending", "Pending")}
                    className="h-5 w-auto"
                    draggable="false"
                  />
                ) : null
              }
            />

            <CardDivider />

            <InfoRow56
              icon={ICON_EMAIL}
              label={t("patient.profile.labels.email", "Email")}
              value={withPlaceholder(
                uiPatient.contact.email,
                t("patient.profile.placeholders.email", "you@youremail.com"),
              )}
              extraRight={
                showEmailVerified ? (
                  <img
                    src={ICON_VERIFIED_CHIP}
                    alt={t("patient.profile.verified", "Verified")}
                    className="h-5 w-auto"
                    draggable="false"
                  />
                ) : null
              }
            />

            <CardDivider />

            <InfoRow56
              icon={ICON_NATIONAL_ID}
              label={t("patient.profile.labels.nationalId", "National ID / Passport")}
              value={withPlaceholder(
                uiPatient.contact.nationalId,
                t("patient.profile.placeholders.nationalId", "Your ID / Passport"),
              )}
              extraRight={
                idIsVerified ? (
                  <img
                    src={ICON_VERIFIED_CHIP}
                    alt={t("patient.profile.verified", "Verified")}
                    className="h-5 w-auto"
                    draggable="false"
                  />
                ) : idIsPending ? (
                  <img
                    src={ICON_PENDING_VERIFICATION}
                    alt={t("patient.profile.pendingVerification", "Pending verification")}
                    className="h-5 w-auto"
                    draggable="false"
                  />
                ) : null
              }
            />
          </Card>
        </div>

            <SectionDivider />

            {/* Health insurance */}
            <div>
          <SectionHeader
            title={t("patient.profile.sections.insurance", "Health insurance")}
            actionLabel={canEdit ? t("patient.profile.actions.addEdit", "Add / Edit") : null}
            onAction={
              canEdit
                ? () => nav("/settings/insurance", { state: { returnTo: "/patient/profile" } })
                : undefined
            }
          />

          <Card className="mt-2 overflow-hidden">
            <InfoRow56
              icon={ICON_HEALTH_INSURANCE_PROFILE}
              label={t("patient.profile.sections.insurance", "Health insurance")}
              value={null}
              largeIcon
              variant="cardHeader"
            />

            <CardDivider />

            <InfoRow56
              icon={ICON_HEALTH_INSURANCE}
              reserveIconSpace
              label=""
              value={
                insuranceSummary
                  ? insuranceSummary
                  : insuranceLoaded
                  ? canEdit
                    ? t("common.comingSoon", "Coming soon")
                    : t("patient.profile.notProvided", "Not provided")
                  : t("common.loading", "Loading...")
              }
              supporting={
                insuranceSummary || !canEdit
                  ? ""
                  : t(
                      "patient.profile.insuranceSupport",
                      "Add your provider and membership details to speed up claims and billing.",
                    )
              }
              variant="row"
            />
          </Card>
        </div>

            <SectionDivider />

            {/* Dependants */}
            <SectionHeader
          title={t("patient.profile.sections.dependants", "Dependants")}
          actionLabel={
            canEdit
              ? dependantsLoaded && dependantsRows.length > 0
                ? editLabel
                : t("patient.profile.actions.addEdit", "Add / Edit")
              : null
          }
          onAction={
            canEdit
              ? () => nav("/settings/dependants", { state: { returnTo: "/patient/profile" } })
              : undefined
          }
        />

        <Card className="mt-2 overflow-hidden">
          <InfoRow56
            icon={ICON_DEPENDANT}
            label={t("patient.profile.sections.dependants", "Dependants")}
            value={
              !dependantsLoaded
                ? t("common.loading", "Loading...")
                : dependantsRows.length === 0
                  ? canEdit
                  ? t("patient.profile.actions.addDependant", "Add dependant")
                  : t("patient.profile.empty.dependants", "No dependants listed")
                : t("patient.profile.dependantsCount", "{count} dependant(s)", {
                    count: dependantsRows.length,
                  })
            }
            largeIcon
            variant="cardHeader"
          />

          {canEdit && dependantsLoaded && dependantsRows.length === 0 && (
            <>
              <CardDivider />
              <button
                type="button"
                className="w-full px-4 py-3 text-left text-primary text-body-medium"
                onClick={() =>
                  nav("/settings/dependants", { state: { returnTo: "/patient/profile" } })
                }
              >
                {t("patient.profile.actions.addDependant", "Add dependant")}
              </button>
            </>
          )}

          {dependantsLoaded && dependantsRows.length > 0 && (
            <div>
              {dependantsRows.slice(0, 5).map((d, idx) => {
                const first = String(d?.first_name || "").trim();
                const last = String(d?.last_name || "").trim();
                const fullName =
                  `${first} ${last}`.trim() ||
                  t("patient.profile.dependantFallback", "Dependant");

                const status = String(d?.identity_verification_status || d?.verification_status || "").toLowerCase();
                const badgeIcon =
                  status === "verified"
                    ? ICON_VERIFIED_CHIP
                    : status === "pending" || status === "pending_upload" || status === "pending_review"
                    ? ICON_PENDING_VERIFICATION
                    : "";
                return (
                  <div key={d?.id || `dep-${idx}`}>
                    <CardDivider />

                    {canEdit ? (
                      <button
                        type="button"
                        className="w-full flex items-center justify-between px-4 pr-12 py-3 text-left"
                        onClick={() =>
                          nav("/settings/dependants", {
                            state: { returnTo: "/patient/profile", openId: d?.id },
                          })
                        }
                      >
                        <div className="flex items-center min-w-0">
                          <span className="mr-3 inline-flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface))] shadow-[0px_0px_0px_1px_rgba(24,50,119,0.10),0px_6px_12px_-4px_rgba(29,79,215,0.06)]">
                            <img
                              src={resolveDependantAvatarSrc(d)}
                              alt=""
                              className="h-full w-full object-cover"
                              draggable="false"
                            />
                          </span>

                          <div className="min-w-0">
                          <div className="text-body-large text-on-surface truncate">
                              {fullName}
                            </div>

                            {String(d?.relationship || "").trim() ? (
                              <div className="text-body-medium text-on-surface-var truncate">
                                {String(d?.relationship || "").trim()}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        {badgeIcon ? (
                          <img
                            src={badgeIcon}
                            alt=""
                            className="h-5 w-auto shrink-0"
                            draggable="false"
                          />
                        ) : (
                          <span className="h-5 w-5 shrink-0" aria-hidden="true" />
                        )}
                      </button>
                    ) : (
                      <div className="w-full flex items-center justify-between px-4 pr-12 py-3 text-left">
                        <div className="flex items-center min-w-0">
                          <span className="mr-3 inline-flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface))] shadow-[0px_0px_0px_1px_rgba(24,50,119,0.10),0px_6px_12px_-4px_rgba(29,79,215,0.06)]">
                            <img
                              src={resolveDependantAvatarSrc(d)}
                              alt=""
                              className="h-full w-full object-cover"
                              draggable="false"
                            />
                          </span>

                          <div className="min-w-0">
                            <div className="text-body-large text-on-surface truncate">
                              {fullName}
                            </div>

                            {String(d?.relationship || "").trim() ? (
                              <div className="text-body-medium text-on-surface-var truncate">
                                {String(d?.relationship || "").trim()}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        {badgeIcon ? (
                          <img
                            src={badgeIcon}
                            alt=""
                            className="h-5 w-auto shrink-0"
                            draggable="false"
                          />
                        ) : (
                          <span className="h-5 w-5 shrink-0" aria-hidden="true" />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

            <SectionDivider />

            {/* Residence address */}
            <div>
          <SectionHeader
            title={t("patient.profile.sections.address", isFrenchLanguageCode(languageCode) ? "Adresse de résidence" : "Residence address")}
            actionLabel={canEdit ? editLabel : null}
            onAction={canEdit ? () => nav("/settings/patient/p/edit-address") : undefined}
          />

          <Card className="mt-2 overflow-hidden">
            <InfoRow56
              icon={ICON_ADDRESS_SECTION}
              label={t("patient.profile.sections.address", isFrenchLanguageCode(languageCode) ? "Adresse de résidence" : "Residence address")}
              value={null}
              largeIcon
              variant="cardHeader"
            />

            <CardDivider />

            <InfoRow56
              icon={ICON_ADDRESS}
              label={t("patient.profile.labels.street", isFrenchLanguageCode(languageCode) ? "Rue" : "Street")}
              value={withPlaceholder(
                uiPatient.address.street,
                t("patient.profile.placeholders.street", isFrenchLanguageCode(languageCode) ? "Votre rue, ville" : "Your street, City"),
              )}
            />

            <CardDivider />

            <InfoRow56
              icon={ICON_POSTAL}
              label={t("patient.profile.labels.postalCode", isFrenchLanguageCode(languageCode) ? "Code postal" : "Postal code")}
              value={withPlaceholder(
                uiPatient.address.postalCode,
                t("patient.profile.placeholders.postalCode", "00000"),
              )}
            />

            <CardDivider />

            <InfoRow56
              icon={ICON_DISTANCE}
              label={t("patient.profile.labels.country", isFrenchLanguageCode(languageCode) ? "Pays" : "Country")}
              value={withPlaceholder(
                uiPatient.address.country,
                t("patient.profile.placeholders.country", isFrenchLanguageCode(languageCode) ? "Pays" : "Country"),
              )}
              supporting={uiPatient.address.distance || ""}
            />
          </Card>
        </div>

            <SectionDivider />
          </>
        ) : (
          <SectionDivider />
        )}

        {/* Medical history */}
        {canSeeMedical ? (
          <div>
            <SectionHeader
              title={
                isDoctorView
                  ? t("patient.profile.sections.clinicalSummary", "Clinical summary")
                  : t("patient.profile.sections.medicalHistory", isFrenchLanguageCode(languageCode) ? "Antécédents médicaux" : "Medical History")
              }
              actionLabel={canEdit ? editLabel : null}
              onAction={canEdit ? () => nav("/settings/patient/p/edit-intro") : undefined}
            />

            <Card className="mt-2 overflow-hidden">
              <InfoRow56
                icon={ICON_MEDICAL_SECTION}
                label={
                  isDoctorView
                    ? t("patient.profile.sections.clinicalSummary", "Clinical summary")
                    : t("patient.profile.sections.medicalHistory", isFrenchLanguageCode(languageCode) ? "Antécédents médicaux" : "Medical History")
                }
                value={null}
                largeIcon
                variant="cardHeader"
              />

              <CardDivider />

              <InfoRow56
                icon={ICON_BLOODTYPE}
                label={t("patient.profile.labels.bloodType", isFrenchLanguageCode(languageCode) ? "Groupe sanguin" : "Blood type")}
                value={withPlaceholder(
                  uiPatient.medical.bloodType,
                  t("patient.profile.unknown", "Unknown"),
                )}
              />

              <CardDivider />

              <InfoRow56
                icon={ICON_ALLERGY}
                  label={t("patient.profile.labels.allergies", isFrenchLanguageCode(languageCode) ? "Allergies" : "Allergies")}
                value={withPlaceholder(
                  uiPatient.medical.allergies,
                  t("patient.profile.noneRecorded", "None recorded"),
                )}
              />

              <CardDivider />

              <InfoRow56
                icon={ICON_CONDITIONS}
                  label={t("patient.profile.labels.chronicConditions", isFrenchLanguageCode(languageCode) ? "Affections chroniques" : "Chronic Conditions")}
                value={withPlaceholder(
                  uiPatient.medical.chronicConditions,
                  t("patient.profile.noneRecorded", "None recorded"),
                )}
              />

              {uiPatient.medical.medications ? (
                <>
                  <CardDivider />
                  <InfoRow56
                    icon={ICON_MEDICATIONS}
                    label={t("patient.profile.labels.medications", isFrenchLanguageCode(languageCode) ? "Médicaments" : "Medications")}
                    value={uiPatient.medical.medications}
                  />
                </>
              ) : null}

              {uiPatient.medical.emergencyAlerts ? (
                <>
                  <CardDivider />
                  <InfoRow56
                    icon={ICON_MEDICAL_SECTION}
                    label={t("patient.profile.labels.emergencyAlerts", isFrenchLanguageCode(languageCode) ? "Alertes d’urgence" : "Emergency alerts")}
                    value={uiPatient.medical.emergencyAlerts}
                  />
                </>
              ) : null}

              {!isDoctorView ? (
                <>
                  <CardDivider />

                  <InfoRow56
                    icon={ICON_PULMONOLOGY}
                    label={t("patient.profile.labels.organDonor", isFrenchLanguageCode(languageCode) ? "Don d’organes" : "Organ Donor")}
                    value={withPlaceholder(
                      uiPatient.medical.organDonor,
                      t("patient.profile.unknown", "Unknown"),
                    )}
                  />

                  <CardDivider />

                  <InfoRow56
                    icon={ICON_HEIGHT_WEIGHT}
                    label={t("patient.profile.labels.heightWeight", isFrenchLanguageCode(languageCode) ? "Taille et poids" : "Height & Weight")}
                    value={withPlaceholder(
                      uiPatient.medical.heightWeight,
                      t("patient.profile.notRecorded", "Not recorded"),
                    )}
                  />
                </>
              ) : null}
            </Card>
          </div>
        ) : (
          <Card className="mt-2 p-4 text-body-medium text-on-surface-var">
            {t(
              "patient.profile.medicalHistoryLocked",
              isFrenchLanguageCode(languageCode)
                ? "Les antécédents médicaux sont verrouillés jusqu’à ce que le patient accorde l’accès au dossier médical."
                : "Medical history is locked until the patient grants EHR access.",
            )}
          </Card>
        )}

        {!isDoctorView && (
          <>
            <SectionDivider />

            {/* Patient photos */}
            <section className="mt-6">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-body-medium text-on-surface-var">
                  {t("patient.profile.sections.photos", "Patient photos")}
                </h2>
                {canEdit ? (
                  <div className="flex items-center gap-1 text-body-medium text-primary">
                    <button
                      type="button"
                      onClick={() => nav("/patient/photos", { state: { patientPhotosAction: "add" } })}
                    >
                      {t("patient.profile.actions.addPhoto", addLabel)}
                    </button>
                    <span aria-hidden="true" className="text-on-surface-var">•</span>
                    <button
                      type="button"
                      onClick={() => nav("/patient/photos", { state: { patientPhotosAction: "edit" } })}
                    >
                      {t("patient.profile.actions.editPhotos", editLabel)}
                    </button>
                    <span aria-hidden="true" className="text-on-surface-var">•</span>
                    <button
                      type="button"
                      onClick={() => nav("/patient/photos")}
                    >
                      {t("common.showAll", "Show all")}
                    </button>
                  </div>
                ) : null}
              </div>

              <div
                className="
                  mt-2
                  flex gap-3 overflow-x-auto no-scrollbar
                  pb-2
                  snap-x snap-mandatory
                "
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                {patientPhotoCards.map((img) => (
                  <div
                    key={img.id}
                    className="
                      snap-start shrink-0
                      w-[261px] h-[283px]
                      rounded-[28px]
                      overflow-hidden
                      bg-neutral-200
                    "
                  >
                    <img
                      src={img.url}
                      alt=""
                      className="w-full h-full object-cover"
                      draggable="false"
                    />
                  </div>
                ))}

                {patientPhotoCards.length === 0 && (
                  <div
                    className="
                      snap-start shrink-0
                      w-[261px] h-[283px]
                      rounded-[28px]
                      overflow-hidden
                      bg-[color-mix(in_oklab,var(--Schemes-Primary,#1D4FD7)_4%,var(--Schemes-Surface,#FFFFFF))]
                      flex items-center justify-center
                      text-body-medium text-on-surface-var
                      px-4 text-center
                    "
                  >
                    {t("patient.profile.empty.photos", "No photos yet.")}
                  </div>
                )}
              </div>
            </section>
          </>
        )}

        {!isDoctorView ? (
          <>
            <SectionDivider />

            {/* Languages */}
            <div>
              <SectionHeader
                title={t("patient.profile.sections.languages", isFrench ? "Langues" : "Languages")}
                actionLabel={canEdit ? editLabel : null}
                onAction={canEdit ? () => nav("/settings/patient/p/languages") : undefined}
              />

              <Card className="mt-2 overflow-hidden">
                <div className="px-4 py-3">
                  {uiPatient.preferredLanguage ? (
                    <div>
                      <div className="text-label-medium text-on-surface-var">
                        {t("settings.subflows.languages.preferredLabel", isFrench ? "Langue préférée / principale" : "Preferred / primary language")}
                      </div>
                      <div className="mt-1 text-body-large text-on-surface">
                        {uiPatient.preferredLanguage}
                      </div>
                      {uiPatient.otherLanguages.length > 0 ? (
                        <div className="mt-3">
                          <div className="text-label-medium text-on-surface-var">
                            {t("patient.profile.languages.otherLabel", isFrench ? "Autres langues" : "Other languages")}
                          </div>
                          <div className="mt-1 text-body-large text-on-surface">
                            {uiPatient.otherLanguages.join(", ")}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="text-body-large text-on-surface">
                      {withPlaceholder(
                        uiPatient.languages,
                        t("patient.profile.placeholders.languages", isFrench ? "Langues préférées" : "Preferred languages"),
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>

            <SectionDivider />

            {/* Notifications */}
            <div>
              <SectionHeader
                title={t("patient.profile.sections.notifications", "Notifications")}
                actionLabel={canEdit ? editLabel : null}
                onAction={canEdit ? () => nav("/settings/patient/p/notifications-news") : undefined}
              />

              <Card className="mt-2 overflow-hidden">
                <div className="px-4 py-3">
                  <div className="text-body-large text-on-surface">
                    {uiPatient.notifications.title}
                  </div>
                  <div className="mt-1 text-body-medium text-on-surface-var">
                    {uiPatient.notifications.channels ||
                      t("patient.profile.notConfigured", "Not configured yet")}
                  </div>
                </div>
              </Card>

              <div className="mt-2 text-body-small text-on-surface-var">
                {t(
                  "patient.profile.notificationsSupport",
                  "Manage how you receive updates about appointments, prescriptions and news.",
                )}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
